/**
 * Khung 7 nhãn ngoại hình nhân vật (đợt 260922 E1-P1) — logic THUẦN, tách khỏi file import antd để jest của
 * react-scripts chạy được (antd v6 không nạp được dưới jest — xem `promptChipUtils.ts`).
 *
 * Bản sao phía FE của `admin-service/src/domains/filmCharacterAppearanceDomain.ts`. Thứ tự nhãn là HỢP ĐỒNG với
 * prompt ảnh: đổi ở đây thì phải đổi cả bên backend.
 */
import type { CharacterAppearance } from '../../types/film';

export type AppearanceKey = keyof CharacterAppearance;

export const APPEARANCE_LABELS: ReadonlyArray<readonly [AppearanceKey, string]> = [
  ['height', 'Height'],
  ['headToBodyRatio', 'Head-to-body ratio'],
  ['ethnicity', 'Ethnicity'],
  ['build', 'Build'],
  ['hair', 'Hair'],
  ['facialFeatures', 'Facial features'],
  ['clothingProps', 'Clothing & props'],
];

/** Gợi ý ngắn cho từng ô nhập — cùng khuôn với hướng dẫn trong template `film_dev`. */
export const APPEARANCE_PLACEHOLDERS: Record<AppearanceKey, string> = {
  height: 'e.g. 165cm',
  headToBodyRatio: 'e.g. 7.5 heads',
  ethnicity: 'e.g. White American, fair skin',
  build: 'tuổi trước, dáng sau — e.g. early 20s, athletic and toned',
  hair: 'e.g. honey-blonde hair in a high ponytail',
  facialFeatures: 'e.g. heart-shaped face, sharp hazel eyes, freckles',
  clothingProps: 'bộ đồ mặc định từ đầu tới GIÀY + đồ mang theo người',
};

const clean = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Các nhãn có giá trị, đúng thứ tự — để hiện trên thẻ. */
export function appearanceLines(app: Partial<Record<AppearanceKey, unknown>> | null | undefined): Array<{ key: AppearanceKey; label: string; value: string }> {
  if (!app) return [];
  return APPEARANCE_LABELS
    .map(([key, label]) => ({ key, label, value: clean(app[key]) }))
    .filter((l) => l.value);
}

export function countFilledLabels(app: Partial<Record<AppearanceKey, unknown>> | null | undefined): number {
  return appearanceLines(app).length;
}

/**
 * Quyết có gửi `appearance` lên `PUT /characters/:id` không:
 * - có nhãn ⇒ gửi object đã trim (backend dựng lại `casting` từ nhãn);
 * - không nhãn nào, nhân vật vốn CHƯA có nhãn ⇒ `undefined` (không gửi) — casting tự viết được giữ y như cũ;
 * - không nhãn nào, nhân vật vốn ĐÃ có nhãn ⇒ `null` — người chủ động xoá, backend xoá nhãn.
 */
export function appearancePayload(
  formValues: Partial<Record<AppearanceKey, unknown>> | null | undefined,
  hadAppearance: boolean,
): CharacterAppearance | null | undefined {
  const lines = appearanceLines(formValues);
  if (lines.length) return Object.fromEntries(lines.map((l) => [l.key, l.value])) as CharacterAppearance;
  return hadAppearance ? null : undefined;
}
